// Reset password — opened from the emailed link (?email=&token=). New password
// with a strength meter + a one-tap strong-password suggestion.

import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Lock, Eye, EyeOff, Sparkles, Check } from 'lucide-react';
import { toast } from 'sonner';
import { auth } from '../../lib/api';
import { suggestPassword, scoreStrength } from '../../lib/password';

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const email = params.get('email') ?? '';
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const strength = useMemo(() => scoreStrength(password), [password]);
  const valid = password.length >= 8 && password === confirm;
  const missing = !email || !token;

  const suggest = () => {
    const pw = suggestPassword(16);
    setPassword(pw); setConfirm(pw); setShow(true);
    navigator.clipboard?.writeText(pw).then(() => toast.success('Strong password generated + copied'), () => {});
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      await auth.resetPassword({ email, token, password });
      toast.success('Password updated — you can sign in now');
      navigate('/login', { replace: true });
    } catch (err) { toast.error((err as Error).message || 'Could not reset password'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
        <div className="text-[20px] font-bold text-brand-700 text-center">Lakay<span className="text-amber-500">Cart</span></div>
        <p className="mt-4 text-[15px] font-semibold text-slate-900 text-center">Choose a new password</p>

        {missing ? (
          <p className="mt-3 text-[12.5px] text-rose-600 text-center">This reset link is incomplete. Request a new one from <Link to="/forgot-password" className="font-semibold underline">Forgot password</Link>.</p>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-3">
            <p className="text-[12px] text-slate-500">For <b>{email}</b></p>

            <label className="block">
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">New password</span>
                <button type="button" onClick={suggest} className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-600 hover:underline"><Sparkles className="w-3 h-3" /> Suggest strong</button>
              </div>
              <div className="mt-1 relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input type={show ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" required minLength={8} placeholder="At least 8 characters"
                  className="w-full pl-9 pr-9 py-2.5 text-[14px] font-mono border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
                <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div>
              {password && (
                <div className="mt-1.5 flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full ${strength.cls} transition-all`} style={{ width: `${(strength.score / 4) * 100}%` }} />
                  </div>
                  <span className="text-[10.5px] font-semibold text-slate-500 w-16 text-right">{strength.label}</span>
                </div>
              )}
            </label>

            <label className="block">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Confirm password</span>
              <div className="mt-1 relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input type={show ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" required placeholder="Re-enter password"
                  className="w-full pl-9 pr-9 py-2.5 text-[14px] font-mono border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
                {confirm && password === confirm && <Check className="w-4 h-4 text-emerald-500 absolute right-2.5 top-1/2 -translate-y-1/2" />}
              </div>
              {confirm && password !== confirm && <p className="mt-1 text-[11px] text-rose-600">Passwords don't match.</p>}
            </label>

            <button type="submit" disabled={busy || !valid} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-brand-600 text-white text-[14px] font-semibold hover:bg-brand-700 disabled:opacity-50">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Update password
            </button>
          </form>
        )}
        <Link to="/login" className="mt-4 inline-block text-[13px] text-slate-500 hover:text-slate-900">Back to sign in</Link>
      </div>
    </div>
  );
}
